import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import "./NestedDropdown.css";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";

function NestedDropdown({
  items = [],
  value = "",
  onSelect = () => {},
  placeholder = "",
  title = "",
  style = {},
  className = "",
  triggerStyle = {},
  menuStyle = {},
  disabled = false,
}) {
  const [rootOpen, setRootOpen] = useState(false);
  const [openPaths, setOpenPaths] = useState([]); // strings like '0' or '0.1'
  const [menuMaxHeight, setMenuMaxHeight] = useState(null);
  const wrapperRef = useRef(null);
  const wrapperStyles = { width: "100%", ...style };
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [otherValue, setOtherValue] = useState("");
  const [localItems, setLocalItems] = useState(items);

  useEffect(() => {
    setLocalItems(items);
  }, [items]);

  const findLabelByValue = (list, searchValue) => {
    for (const item of list) {
      if (item.value === searchValue) {
        return item.label;
      }
      if (item.children) {
        const found = findLabelByValue(item.children, searchValue);
        if (found) return found;
      }
    }
    return "";
  };

  const selectedLabel = useMemo(() => {
    return value ? findLabelByValue(localItems, value) : "";
  }, [items, value]);

  const titleText = title || placeholder;
  const floatingActive = Boolean(selectedLabel || rootOpen);

  useEffect(() => {
    function handleOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setRootOpen(false);
        setOpenPaths([]);
      }
    }
    function updateMenuHeight() {
      if (wrapperRef.current) {
        const incidentForm = wrapperRef.current.closest(
          ".MuiDialogContent-root",
        );
        if (incidentForm) {
          setMenuMaxHeight(incidentForm.clientHeight * 0.7);
        }
      }
    }
    document.addEventListener("mousedown", handleOutside);
    window.addEventListener("resize", updateMenuHeight);
    updateMenuHeight();
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("resize", updateMenuHeight);
    };
  }, []);

  const togglePath = (path) => {
    setOpenPaths((prev) => {
      const isOpen = prev.includes(path);
      if (isOpen) {
        return prev.filter((p) => p !== path && !p.startsWith(`${path}.`));
      }

      const parent = path.includes(".")
        ? path.slice(0, path.lastIndexOf("."))
        : "";
      return [
        ...prev.filter((p) => {
          const pParent = p.includes(".") ? p.slice(0, p.lastIndexOf(".")) : "";
          return pParent !== parent;
        }),
        path,
      ];
    });
  };

  const handleSelect = (item) => {
    setRootOpen(false);
    setOpenPaths([]);
    onSelect(item);
  };

  const renderMenu = (list, prefix = "") => (
    <ul className="nd-menu">
      {list.map((it, i) => {
        const path = prefix === "" ? `${i}` : `${prefix}.${i}`;
        const hasChildren =
          Array.isArray(it.children) && it.children.length > 0;
        const isOpen = openPaths.includes(path);

        return (
          <li className="nd-item" key={path}>
            <button
              type="button"
              className={`nd-item-label ${hasChildren ? "nd-has-children" : ""} ${!hasChildren && it.value === value ? "nd-item-selected" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                if (hasChildren) togglePath(path);
                else handleSelect(it);
              }}
            >
              <span className="nd-label-text">{it.label}</span>
              {hasChildren && (
                <span className={`nd-arrow ${isOpen ? "open" : ""}`}>
                  <ArrowRightIcon />
                </span>
              )}
            </button>
            {hasChildren && isOpen && (
              <div className="nd-submenu">{renderMenu(it.children, path)}</div>
            )}
          </li>
        );
      })}
    </ul>
  );

  const handleOnClose = (e) => {
    e.stopPropagation();
    setOtherValue("");
    setShowOtherInput(false);
  };

  const handleTickClick = (e) => {
    e.stopPropagation();

    const value = otherValue.trim();

    if (!value) return;

    const exists = localItems.some(
      (item) => item.label.toLowerCase() === value.toLowerCase(),
    );

    if (exists) {
      const existing = localItems.find(
        (item) => item.label.toLowerCase() === value.toLowerCase(),
      );

      onSelect(existing);
    } else {
      const customItem = { label: value, value };

      setLocalItems((prev) => [...prev, customItem]);
      onSelect(customItem);
    }

    setOtherValue("");
    setShowOtherInput(false);
    setRootOpen(false);
  };

  return (
    <div
      className={`nd-wrapper ${className}`}
      ref={wrapperRef}
      style={wrapperStyles}
    >
      <button
        type="button"
        className={`nd-trigger ${rootOpen ? "active" : ""}`}
        onClick={() => !disabled && setRootOpen((v) => !v)}
        aria-expanded={rootOpen}
        style={triggerStyle}
        disabled={disabled}
      >
        {!!titleText && (
          <span
            className={`nd-floating-label ${floatingActive ? "active" : ""} ${rootOpen ? "nd-color-text" : ""}`}
          >
            {titleText}
          </span>
        )}
        <span
          className={`nd-trigger-text ${selectedLabel ? "nd-selected" : "nd-placeholder"}`}
        >
          {selectedLabel}
        </span>
        <div className={`nd-trigger-arrow ${rootOpen ? "open" : ""}`}>
          <ArrowDropDownIcon color="#0000008a" />
        </div>
      </button>

      {rootOpen && (
        <div
          className="nd-root-menu"
          style={{ maxHeight: menuMaxHeight || "80vh", ...menuStyle }}
        >
          {renderMenu(localItems, "")}
          <div className="nd-other-container">
            {!showOtherInput ? (
              <button
                type="button"
                className="nd-item-label"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowOtherInput(true);
                }}
              >
                Others
              </button>
            ) : (
              <div className="nd-other-input-wrapper">
                <TextField
                  size="small"
                  autoFocus
                  placeholder="Enter Page Name"
                  value={otherValue}
                  onChange={(e) => setOtherValue(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />

                <IconButton
                  size="small"
                  color="success"
                  onClick={handleTickClick}
                >
                  <CheckIcon />
                </IconButton>

                <IconButton size="small" color="error" onClick={handleOnClose}>
                  <CloseIcon />
                </IconButton>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

NestedDropdown.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
      ]),
      children: PropTypes.array,
    }),
  ),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
  onSelect: PropTypes.func,
  placeholder: PropTypes.string,
  title: PropTypes.string,
  style: PropTypes.object,
  className: PropTypes.string,
  triggerStyle: PropTypes.object,
  menuStyle: PropTypes.object,
  disabled: PropTypes.bool,
};

NestedDropdown.defaultProps = {
  items: [],
  value: "",
  onSelect: () => {},
  placeholder: "",
  title: "",
  style: {},
  className: "",
  triggerStyle: {},
  menuStyle: {},
  disabled: false,
};

export default NestedDropdown;
